import { formatCostAmount, formatVolume, NO_DATA_TEXT } from "./format";

export interface ConsumptionReportGridRow {
	[field: string]: string | number | boolean | null | undefined;
	id: string;
	period?: string;
	mill?: string;
	section?: string;
	event?: string;
	eventStart?: string;
	eventEnd?: string;
	valuePerTon?: number | null;
	valuePerBillet?: number | null;
	value: number | null;
	costPerTon?: number | null;
	cost: number | null;
	isCostApproximate: boolean;
	isValuePartial: boolean;
	isValueApproximate: boolean;
	isCostPartial: boolean;
	missingMeters: string;
}

interface ConsumptionReportGridRowInput {
	id: string;
	period?: string;
	mill?: string;
	section?: string;
	event?: string;
	eventStart?: string;
	eventEnd?: string;
	valuePerTon?: number | null;
	valuePerBillet?: number | null;
	meterIds: string[];
	meterValues: Record<string, number | null>;
	meterCoverage: Record<string, number | null>;
	meterPartial: Record<string, boolean>;
	value: number | null;
	costPerTon?: number | null;
	cost: number | null;
	isCostApproximate: boolean;
	isValuePartial: boolean;
	isValueApproximate: boolean;
	isCostPartial: boolean;
}

interface ConsumptionReportTotalRowInput {
	meterIds: string[];
	meterTotalValue: Record<string, number | null>;
	meterTotalPartial: Record<string, boolean>;
	grandTotalValue: number | null;
	grandValuePerTon: number | null;
	grandValuePerBillet: number | null;
	grandTotalCost: number | null;
	grandCostPerTon: number | null;
	isCostApproximate: boolean;
	isValuePartial: boolean;
	isValueApproximate: boolean;
	isCostPartial: boolean;
	isSection: boolean;
}

export const getConsumptionReportMeterField = (index: number) =>
	`meter_${index}`;

export const getConsumptionReportMeterCoverageField = (index: number) =>
	`meter_${index}_coverage`;

export const getConsumptionReportMeterPartialField = (index: number) =>
	`meter_${index}_partial`;

export const formatConsumptionReportValue = (
	value: number | null | undefined,
	isApproximate?: boolean | null,
) => {
	const amount = formatVolume(value);
	if (amount === null) return NO_DATA_TEXT;
	return isApproximate ? `≈${amount}` : amount;
};

export const formatConsumptionReportCost = (
	value: number | null | undefined,
	isApproximate: boolean | null | undefined,
) => {
	const amount = formatCostAmount(value);
	if (amount === null) return NO_DATA_TEXT;
	return isApproximate ? `≈${amount}` : amount;
};

export const createConsumptionReportGridRow = ({
	meterIds,
	meterValues,
	meterCoverage,
	meterPartial,
	...row
}: ConsumptionReportGridRowInput): ConsumptionReportGridRow => {
	const missingMeterIds = meterIds.filter(
		(meterId) =>
			meterValues[meterId] === null || meterValues[meterId] === undefined,
	);
	const gridRow: ConsumptionReportGridRow = {
		...row,
		missingMeters: missingMeterIds.join(", "),
	};
	meterIds.forEach((meterId, index) => {
		gridRow[getConsumptionReportMeterField(index)] =
			meterValues[meterId] ?? null;
		gridRow[getConsumptionReportMeterCoverageField(index)] =
			meterCoverage[meterId] ?? null;
		gridRow[getConsumptionReportMeterPartialField(index)] =
			meterPartial[meterId] ?? false;
	});
	return gridRow;
};

export const createConsumptionReportTotalRow = ({
	meterIds,
	meterTotalValue,
	meterTotalPartial,
	grandTotalValue,
	grandValuePerTon,
	grandValuePerBillet,
	grandTotalCost,
	grandCostPerTon,
	isCostApproximate,
	isValuePartial,
	isValueApproximate,
	isCostPartial,
	isSection,
}: ConsumptionReportTotalRowInput): ConsumptionReportGridRow =>
	createConsumptionReportGridRow({
		id: "total",
		period: isSection ? undefined : "Total",
		mill: undefined,
		section: undefined,
		event: isSection ? "Total" : undefined,
		eventStart: undefined,
		eventEnd: undefined,
		valuePerTon: grandValuePerTon,
		valuePerBillet: grandValuePerBillet,
		meterIds,
		meterValues: meterTotalValue,
		value: grandTotalValue,
		costPerTon: grandCostPerTon,
		cost: grandTotalCost,
		meterCoverage: {},
		meterPartial: meterTotalPartial,
		isCostApproximate,
		isValuePartial,
		isValueApproximate,
		isCostPartial,
	});
